import { useState } from "react";
import { VoiceRecorder } from "./VoiceRecorder";
import { TextInput } from "./TextInput";
import { api } from "../../lib/api";
import type { DailyCheckin } from "../../../../shared/types";

interface CheckinFormProps {
  brandId: string;
  onCheckinComplete?: (checkin: DailyCheckin) => void;
}

type InputMode = "voice" | "text";

export function CheckinForm({ brandId, onCheckinComplete }: CheckinFormProps) {
  const [mode, setMode] = useState<InputMode>("voice");
  const [submitting, setSubmitting] = useState(false);

  async function handleVoiceRecorded(blob: Blob) {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", blob, "recording.webm");
      formData.append("brand_id", brandId);
      const result = await api.upload<{ data: DailyCheckin }>("/api/input/voice", formData);
      onCheckinComplete?.(result.data);
    } catch (err) {
      console.error("Voice upload failed:", err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTextSubmit(text: string) {
    setSubmitting(true);
    try {
      const result = await api.post<{ data: DailyCheckin }>("/api/input/text", {
        text,
        brand_id: brandId,
      });
      onCheckinComplete?.(result.data);
    } catch (err) {
      console.error("Text check-in failed:", err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-700">Daily check-in</h3>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
          <button
            type="button"
            onClick={() => setMode("voice")}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              mode === "voice" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Voice
          </button>
          <button
            type="button"
            onClick={() => setMode("text")}
            className={`rounded-md px-3 py-1 text-xs font-medium ${
              mode === "text" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Text
          </button>
        </div>
      </div>

      {mode === "voice" ? (
        <VoiceRecorder onRecorded={handleVoiceRecorded} disabled={submitting} />
      ) : (
        <TextInput onSubmit={handleTextSubmit} disabled={submitting} />
      )}
    </div>
  );
}
